import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
  onSnapshot,
  limit,
  orderBy,
} from "firebase/firestore";
import { ref, get, onValue, set } from "firebase/database";
import { auth, firestore, database } from "./firebaseConfig";
import { v4 as uuidv4 } from "uuid";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userSubscription, setUserSubscription] = useState(0);
  const [userSubscriptionDate, setUserSubscriptionDate] = useState(null);
  const [userLanguage, setUserLanguage] = useState("en");
  const [userBooks, setUserBooks] = useState([]);
  const [userLastBookDoc, setUserLastBookDoc] = useState(null);
  const [onlineBooks, setOnlineBooks] = useState([]);
  const [onlineLastBookDoc, setOnlineLastBookDoc] = useState(null);
  const [userBookBeingGenerated, setUserBookBeingGenerated] = useState(false);
  const [userNewBooks, setUserNewBooks] = useState(false);
  const [userLikedBooks, setUserLikedBooks] = useState([]);
  const [userBookTokens, setUserBookTokens] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      fetchData(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const fetchData = async (currentUser) => {
    if (currentUser) {
      const docRef = await getDoc(doc(firestore, "users", currentUser.uid));
      const bookGeneratingRef = doc(firestore, "bookSessions", currentUser.uid);
      const bookSession = await getDoc(bookGeneratingRef);
      const sessionRef = ref(database, `users/${currentUser.uid}`);
      const deviceId = localStorage.getItem("deviceId");

      if (bookSession.exists()) {
        setUserBookBeingGenerated(true);

        onSnapshot(bookGeneratingRef, (docSnapshot) => {
          if (!docSnapshot.exists()) {
            setUserBookBeingGenerated(false);
            setUserNewBooks(true);
          }
        });
      }

      const currentDeviceRef = await get(sessionRef);

      const currentDevice = currentDeviceRef.exists()
        ? currentDeviceRef.val()["deviceId"]
        : null;

      if (!deviceId) {
        const newDeviceId = uuidv4();
        localStorage.setItem("deviceId", newDeviceId);
        await set(sessionRef, { deviceId: newDeviceId });
      } else if (deviceId != currentDevice) {
        await set(sessionRef, { deviceId });
      }

      onValue(sessionRef, (snapshot) => {
        const sessionData = snapshot.val();

        if (
          sessionData &&
          sessionData.deviceId !== localStorage.getItem("deviceId")
        ) {
          auth.signOut();
          window.location.reload();
        }
      });

      if (docRef.exists()) {
        setUserData(docRef.data());
        setUserLanguage(docRef.data()["language"] ?? "en");
        setUserBookTokens(docRef.data()["bookTokens"] ?? 0);
        setUserSubscriptionDate(docRef.data()["subscription"]);
        setUserLikedBooks(docRef.data()["likedBooks"] ?? []);

        const booksRef = query(
          collection(firestore, "books"),
          where("userId", "==", currentUser.uid),
          orderBy("likes", "desc"),
          orderBy("createdAt", "desc"),
          limit(10)
        );

        const booksSnapshot = await getDocs(booksRef);
        const bookList = [];

        setUserLastBookDoc(booksSnapshot.docs[booksSnapshot.docs.length - 1]);

        booksSnapshot.forEach((doc) => {
          bookList.push({
            id: doc.id,
            author: doc.data()["author"],
            title: doc.data()["title"],
            content: doc.data()["book"],
            scenario: doc.data()["scenario"],
            likes: doc.data()["likes"],
            languageIndex: doc.data()["languageIndex"],
          });
        });

        setUserBooks(bookList);

        const subscriptionResponse = await fetch(
          "https://checksubscription-ihg7hu5nyq-ew.a.run.app",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: currentUser.uid,
            }),
          }
        );
        const resJson = await subscriptionResponse.json();

        setUserSubscription(resJson.daysLeft);
      }

      if (deviceId != currentDevice) {
        console.log("Data being fetched!");
        const scenarioSnapshot = await get(
          ref(database, `sessions/${currentUser.uid}`)
        );

        if (scenarioSnapshot.exists()) {
          const snapshotMap = Object.entries(scenarioSnapshot.val());
          const scenarioContent = Object.entries(snapshotMap[0][1]);
          const messages = [];
          const latestMessage = {};
          let summary = "";

          localStorage.setItem("pages", scenarioContent.length - 1);

          for (let i = scenarioContent.length - 1; i > 0; i--) {
            const content = scenarioContent[i][1];

            messages.unshift(
              { role: "user", content: content["prompt"] },
              { role: "assistant", content: content["response"]["story"] }
            );

            if (i == scenarioContent.length - 1) {
              latestMessage["location"] = content["response"]["location"];
              latestMessage["status"] = content["response"]["status"];
              latestMessage["relations"] = content["response"]["relations"];
              latestMessage["items"] = content["response"]["items"];
            }

            if (content["summary"]) {
              summary = content["summary"];
              break;
            }
          }

          localStorage.setItem("scenario", snapshotMap[0][0]);
          localStorage.setItem(
            "languageIndex",
            scenarioContent[0][1]["languageIndex"]
          );
          localStorage.setItem("summary", summary);
          localStorage.setItem("summaryIndex", 1);
          summary
            ? localStorage.setItem(
                "messages",
                JSON.stringify([
                  { role: "assistant", content: summary },
                  ...messages,
                ])
              )
            : localStorage.setItem("messages", JSON.stringify([...messages]));
          localStorage.setItem("currentLocation", latestMessage["location"]);
          localStorage.setItem("status", latestMessage["status"]);
          localStorage.setItem(
            "relations",
            JSON.stringify(latestMessage["relations"] ?? {})
          );
          localStorage.setItem(
            "items",
            JSON.stringify(latestMessage["items"] ?? {})
          );
        }
      }
    }
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        setUserData,
        userLanguage,
        setUserLanguage,
        userSubscription,
        setUserSubscription,
        userSubscriptionDate,
        userBooks,
        setUserBooks,
        userLastBookDoc,
        setUserLastBookDoc,
        onlineBooks,
        setOnlineBooks,
        onlineLastBookDoc,
        setOnlineLastBookDoc,
        userBookBeingGenerated,
        setUserBookBeingGenerated,
        userNewBooks,
        setUserNewBooks,
        userBookTokens,
        setUserBookTokens,
        userLikedBooks,
        setUserLikedBooks,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }

  return context;
};
